import React, { useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  ShieldCheck,
} from 'lucide-react';

import { useUser } from '../../contexts/UserContext';
import dayjs from '../../lib/day';
import PrivateLayout from '../layouts/PrivateLayout';
import { useFetchPaymentPlanSummary } from '../../services/billing';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount) || 0);

const formatCents = (cents) => formatCurrency((Number(cents) || 0) / 100);

const getPaymentPlanDownPayment = (plan) =>
  (plan?.installments || []).find(
    (installment) => Number(installment.sequence) === 0,
  ) || null;

const isPaymentPlanDepositProcessing = (plan) => {
  if (!plan) return false;
  const downPayment = getPaymentPlanDownPayment(plan);
  return (
    plan.metadata?.depositStatus === 'processing' ||
    downPayment?.status === 'processing'
  );
};

const riseIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.2);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(22, 163, 74, 0);
  }
`;

const PageShell = styled.div`
  min-height: calc(100vh - 120px);
  background: #f7f8fb;
  margin: -0.25rem -0.25rem 0;
  padding: 2rem 1rem;
`;

const ConfirmationFrame = styled.div`
  max-width: 880px;
  margin: 0 auto;
  animation: ${riseIn} 240ms ease-out;
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border: 1px solid #dce6f2;
  border-radius: 18px;
  background:
    radial-gradient(
      circle at top right,
      rgba(79, 70, 229, 0.12),
      transparent 34%
    ),
    linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  padding: 2rem;
  box-shadow: 0 20px 55px rgba(15, 28, 51, 0.08);

  @media (max-width: 720px) {
    padding: 1.35rem;
  }
`;

const StatusMark = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 999px;
  background: ${({ $confirmed }) => ($confirmed ? '#16a34a' : '#4f46e5')};
  color: #ffffff;
  margin-bottom: 1rem;
  animation: ${({ $confirmed }) => ($confirmed ? pulse : 'none')} 1.8s
    ease-in-out infinite;
`;

const HeroCopy = styled.div`
  max-width: 620px;

  .kicker {
    color: #64748b;
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 800;
    margin-bottom: 0.4rem;
  }

  h1 {
    color: #0f1c33;
    font-size: clamp(1.65rem, 3vw, 2.45rem);
    line-height: 1.08;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.02em;
  }

  p {
    color: #66758a;
    font-size: 1rem;
    line-height: 1.55;
    margin: 0.85rem 0 0;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  margin-top: 1.4rem;

  .btn {
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    font-weight: 650;
  }
`;

const PollingStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.88rem;
  font-weight: 650;

  .spinner-border {
    width: 1rem;
    height: 1rem;
    border-width: 0.15em;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const DetailTile = styled.div`
  border: 1px solid #e4eaf3;
  border-radius: 12px;
  background: #ffffff;
  padding: 1rem;

  .label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #7a8699;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    font-weight: 800;
    margin-bottom: 0.35rem;
  }

  .value {
    color: #0f1c33;
    font-size: 1.25rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .hint {
    color: #7a8699;
    font-size: 0.82rem;
    margin-top: 0.2rem;
  }
`;

const PaymentPlanConfirmationPage = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasPermission } = useUser();
  const isAdmin = user?.userType === 'admin' || hasPermission('billing:manage');
  const backPath = isAdmin
    ? `/admin/profile/${claimId}?tab=billing`
    : '/dashboard?tab=billing';
  // Per-invoice plans: a claim can hold siblings, so the Stripe return URL
  // (and the saved-card path) tag the confirmation with ?plan=<id>. Select
  // that plan from plans[]; currentPlan stays as the deep back-compat
  // fallback for old links.
  const requestedPlanId = searchParams.get('plan');
  const selectPlan = (data) => {
    if (!data) return null;
    if (requestedPlanId) {
      const match = (data.plans || []).find(
        (candidate) => candidate.paymentPlanId === requestedPlanId,
      );
      if (match) return match;
    }
    return data.currentPlan || null;
  };
  const { data: summary } = useFetchPaymentPlanSummary(claimId, {
    enabled: !!claimId,
    refetchInterval: (data) => {
      const plan = selectPlan(data);
      const status = plan?.status;
      if (!status) return 2500;
      if (isPaymentPlanDepositProcessing(plan)) return 15000;
      return status === 'setup_pending' ? 2500 : false;
    },
  });

  const plan = selectPlan(summary);
  const status = plan?.status;
  const isConfirmed = ['active', 'completed'].includes(status);
  const isBankPaymentProcessing = isPaymentPlanDepositProcessing(plan);
  const isProcessing = !status || status === 'setup_pending';
  const depositVerification = plan?.metadata?.depositVerification || null;
  const needsBankVerification =
    isBankPaymentProcessing && !!depositVerification?.verificationUrl;
  const verificationArrival = depositVerification?.arrivalDate
    ? dayjs(Number(depositVerification.arrivalDate) * 1000).format('MMM D')
    : null;
  const firstInstallment = useMemo(
    () =>
      (plan?.installments || [])
        .filter((installment) => Number(installment.sequence) > 0)
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))[0],
    [plan?.installments],
  );

  const title = isConfirmed
    ? 'Your payment plan is ready'
    : needsBankVerification
      ? 'Verify your bank account'
      : isBankPaymentProcessing
        ? 'Bank payment processing'
        : isProcessing
          ? 'Finalizing your payment plan'
          : 'Payment plan status updated';
  const body = isConfirmed
    ? 'Your payment plan has been setup. You can return to billing to review the active plan and upcoming installment dates.'
    : needsBankVerification
      ? depositVerification.microdepositType === 'amounts'
        ? `Stripe sent two small test deposits to your bank account${verificationArrival ? ` (expected by ${verificationArrival})` : ''}. Enter the two amounts on Stripe's secure verification page to finish setting up your payment plan.`
        : `Stripe sent a $0.01 test deposit to your bank account${verificationArrival ? ` (expected by ${verificationArrival})` : ''}. Find the code starting with "SM" (for example SM1234) in that deposit's bank statement description, then confirm it on Stripe's secure verification page to finish setting up your payment plan.`
      : isBankPaymentProcessing
        ? 'Your bank payment was submitted. ACH payments can take a few business days to confirm, and we will activate the schedule after Stripe confirms the payment.'
        : isProcessing
          ? 'Checkout is complete. We are waiting for the confirmation event to finish activating the plan.'
          : 'Return to billing to review the latest plan status.';

  return (
    <PrivateLayout>
      <PageShell>
        <ConfirmationFrame>
          <Hero>
            <StatusMark $confirmed={isConfirmed}>
              {isConfirmed ? (
                <CheckCircle2 size={28} />
              ) : (
                <ShieldCheck size={27} />
              )}
            </StatusMark>
            <HeroCopy>
              <div className="kicker">
                {isConfirmed
                  ? 'Payment plan confirmed'
                  : 'Stripe confirmation received'}
              </div>
              <h1>{title}</h1>
              {plan?.sourceInvoice?.label ? (
                // Per-invoice plans: name the invoice this plan covers.
                <p
                  style={{
                    fontWeight: 700,
                    color: '#4f46e5',
                    marginBottom: '0.35rem',
                  }}
                >
                  For the {plan.sourceInvoice.label}
                </p>
              ) : null}
              <p>{body}</p>
            </HeroCopy>
            <ActionRow>
              {needsBankVerification ? (
                <Button
                  variant="primary"
                  href={depositVerification.verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Verify bank account
                </Button>
              ) : null}
              <Button
                variant={
                  needsBankVerification ? 'outline-secondary' : 'primary'
                }
                onClick={() => navigate(backPath)}
              >
                <ArrowLeft size={15} className="me-1" />
                Back to billing
              </Button>
              {isProcessing ? (
                <PollingStatus role="status" aria-live="polite">
                  <Spinner animation="border" variant="secondary" />
                  <span>
                    {isBankPaymentProcessing
                      ? 'Checking for bank payment confirmation...'
                      : 'Checking payment plan status...'}
                  </span>
                </PollingStatus>
              ) : null}
            </ActionRow>
          </Hero>

          <DetailGrid>
            <DetailTile>
              <div className="label">
                <CreditCard size={13} />
                Today
              </div>
              <div className="value">
                {formatCents(plan?.downPaymentCents || 0)}
              </div>
              <div className="hint">
                {isBankPaymentProcessing
                  ? 'Bank payment submitted'
                  : Number(plan?.downPaymentCents || 0) > 0
                    ? 'Down payment handled by Stripe'
                    : 'Card saved for future installments'}
              </div>
            </DetailTile>
            <DetailTile>
              <div className="label">
                <CalendarDays size={13} />
                First installment
              </div>
              <div className="value">
                {firstInstallment?.dueDate
                  ? dayjs(firstInstallment.dueDate).format('MMM D')
                  : 'Pending'}
              </div>
              <div className="hint">
                {firstInstallment?.amountCents
                  ? `${formatCents(firstInstallment.amountCents)} scheduled`
                  : 'Schedule will appear after activation'}
              </div>
            </DetailTile>
            <DetailTile>
              <div className="label">
                <ShieldCheck size={13} />
                Plan
              </div>
              <div className="value">
                {plan?.termMonths ? `${plan.termMonths} months` : 'Pending'}
              </div>
              <div className="hint">
                {plan?.remainingCents != null
                  ? `${formatCents(plan.remainingCents)} remaining`
                  : 'Billing will show the latest balance'}
              </div>
            </DetailTile>
          </DetailGrid>
        </ConfirmationFrame>
      </PageShell>
    </PrivateLayout>
  );
};

export default PaymentPlanConfirmationPage;
